import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { TextField, AutoTextarea, Pills } from "@/components/tools/fields";
import { ErrorText, SelectField } from "@/components/rc/shared";

const Thanks = ({ testid, children }) => (
  <div role="status" className="rounded-[2rem] border-2 border-line bg-surface p-8" data-testid={testid}>
    <CheckCircle2 size={36} className="text-brand" aria-hidden />
    <p className="mt-4 font-display text-3xl font-medium">Thank you.</p>
    <p className="mt-2 text-ink-soft">{children}</p>
  </div>
);

const Honeypot = ({ value, onChange }) => (
  <div className="hidden" aria-hidden="true"><label htmlFor="hp-website">Website</label><input id="hp-website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} /></div>
);

const SECTIONS = ["", "SENDCO Health Check", "SEND Provision Review", "SEND Register Health Review", "Attendance & Inclusion Review", "Parent Partnership", "EHCP Annual Review Toolkit",
  "SENDCO Workload Review", "Governor SEND Questions", "SEND Development Planner", "Action Tracker", "SEND Improvement Journal", "30-Day Sprint Planner", "90-Day Planner", "Inspection Findings to Action", "Classroom printables", "Toolkit 3 framework"];
const CATS = ["Workbook feedback", "Improvement suggestion", "Feature request", "Resource request"];

const useForm = (init, url, toBody, validate) => {
  const [f, setF] = useState(init);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const problem = validate?.(f);
    if (problem) return setError(problem);
    setBusy(true);
    setError("");
    try {
      await api.post(url, toBody(f));
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return { f, set, submit, error, done, busy };
};

export const FeedbackForm = () => {
  const { f, set, submit, error, done, busy } = useForm({ category: "Workbook feedback", most_useful: "", improve: "", resources_requested: "", school: "", email: "", website: "" },
    "/feedback", (x) => ({ ...x, pack: "workbook", email: x.email || null }), (x) => (!x.most_useful && !x.improve.trim() && !x.resources_requested.trim() ? "Please answer at least one question." : ""));
  if (done) return <Thanks testid="feedback-thanks">Your feedback goes straight to our team and shapes what we build next.</Thanks>;
  return (
    <form onSubmit={submit} noValidate className="space-y-5 rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8" data-testid="feedback-form">
      <Pills legend="What kind of feedback is this?" hideLegend={false} name="fb-cat" value={f.category} onChange={set("category")} testid="feedback-category" options={CATS.map((c) => ({ v: c, label: c }))} />
      <SelectField id="fb-useful" label="Most useful section" value={f.most_useful} onChange={set("most_useful")} options={SECTIONS} testid="feedback-most-useful" />
      <AutoTextarea id="fb-improve" label="What could be improved?" rows={4} value={f.improve} onChange={set("improve")} testid="feedback-improve" />
      <AutoTextarea id="fb-resources" label="Additional resources or features you'd like" rows={4} value={f.resources_requested} onChange={set("resources_requested")} testid="feedback-resources" />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="fb-school" label="School name (optional)" value={f.school} onChange={set("school")} testid="feedback-school" />
        <TextField id="fb-email" label="Email (optional, only if you'd like a reply)" type="email" value={f.email} onChange={set("email")} testid="feedback-email" />
      </div>
      <Honeypot value={f.website} onChange={set("website")} />
      <p className="text-sm text-ink-soft">You can leave every optional field blank to stay anonymous. Please don't include any pupil information.</p>
      <ErrorText error={error} testid="feedback-error" />
      <button type="submit" disabled={busy} className="btn btn-brand" data-testid="feedback-submit"><Send size={16} aria-hidden /> {busy ? "Sending…" : "Send feedback"}</button>
    </form>
  );
};

const YN3 = (a) => a.map((v) => ({ v, label: v }));
const AREAS = ["SEND leadership", "Provision", "SEND register", "Attendance", "Parent partnership", "EHCP reviews", "Workload", "Governance", "Staff confidence", "Inspection readiness"];

export const ImpactForm = () => {
  const { f, set, submit, error, done, busy } = useForm({ actions_implemented: "", improvements_observed: "", recommend: "", improvement_areas: [], actions_detail: "", remaining_challenges: "", support_requested: "", impact_summary: "", case_study_ok: false, school: "", email: "", website: "" },
    "/impact", (x) => x, (x) => (!x.actions_implemented || !x.improvements_observed || !x.recommend ? "Please answer the three Yes / No questions." : ""));
  if (done) return <Thanks testid="impact-thanks">Your review helps us show what works for schools and plan the next resources.</Thanks>;
  const toggle = (a) => set("improvement_areas")(f.improvement_areas.includes(a) ? f.improvement_areas.filter((x) => x !== a) : [...f.improvement_areas, a]);
  return (
    <form onSubmit={submit} noValidate className="space-y-6 rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8" data-testid="impact-form">
      <Pills legend="Have actions been implemented?" hideLegend={false} name="im-actions" value={f.actions_implemented} onChange={set("actions_implemented")} options={YN3(["Yes", "Partly", "Not yet"])} testid="impact-actions" />
      <AutoTextarea id="im-actions-detail" label="Which actions?" rows={3} value={f.actions_detail} onChange={set("actions_detail")} testid="impact-actions-detail" />
      <Pills legend="Have improvements been observed?" hideLegend={false} name="im-improve" value={f.improvements_observed} onChange={set("improvements_observed")} options={YN3(["Yes", "Partly", "Not yet"])} testid="impact-improvements" />
      <Pills legend="Would you recommend this workbook?" hideLegend={false} name="im-rec" value={f.recommend} onChange={set("recommend")} options={YN3(["Yes", "Maybe", "No"])} testid="impact-recommend" />
      <fieldset>
        <legend className="field-label mb-2">Improvement areas (tick any)</legend>
        <div className="flex flex-wrap gap-2">
          {AREAS.map((a) => <button key={a} type="button" aria-pressed={f.improvement_areas.includes(a)} onClick={() => toggle(a)} className={`chip ${f.improvement_areas.includes(a) ? "bg-brand text-brand-fg" : ""}`} data-testid={`impact-area-${a.replace(/\W+/g, "-").toLowerCase()}`}>{a}</button>)}
        </div>
      </fieldset>
      <AutoTextarea id="im-challenges" label="Remaining challenges" rows={3} value={f.remaining_challenges} onChange={set("remaining_challenges")} testid="impact-challenges" />
      <AutoTextarea id="im-support" label="Additional support required" rows={3} value={f.support_requested} onChange={set("support_requested")} testid="impact-support" />
      <AutoTextarea id="im-summary" label="Impact summary" rows={4} value={f.impact_summary} onChange={set("impact_summary")} testid="impact-summary" />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="im-school" label="School name (optional)" value={f.school} onChange={set("school")} testid="impact-school" />
        <TextField id="im-email" label="Email (optional)" type="email" value={f.email} onChange={set("email")} testid="impact-email" />
      </div>
      <label className="flex items-start gap-3 font-bold"><input type="checkbox" checked={f.case_study_ok} onChange={(e) => set("case_study_ok")(e.target.checked)} className="mt-1 h-5 w-5 accent-[var(--brand)]" data-testid="impact-case-study" />You may contact us about sharing this as an anonymised case study.</label>
      <Honeypot value={f.website} onChange={set("website")} />
      <ErrorText error={error} testid="impact-error" />
      <button type="submit" disabled={busy} className="btn btn-brand" data-testid="impact-submit"><Send size={16} aria-hidden /> {busy ? "Sending…" : "Submit review"}</button>
    </form>
  );
};

const TOPICS = ["General question", "Workbook or toolkits", "Training or support", "Data protection", "Partnership"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactForm = () => {
  const { f, set, submit, error, done, busy } = useForm({ name: "", email: "", organisation: "", topic: TOPICS[0], message: "", website: "" },
    "/contact", (x) => x, (x) => {
      if (x.name.trim().length < 2) return "Please enter your name.";
      if (!EMAIL_RE.test(x.email.trim())) return "Please enter a valid email address so we can reply.";
      if (x.message.trim().length < 10) return "Please write a message of at least 10 characters.";
      return "";
    });
  if (done) return <Thanks testid="contact-thanks">Your message has reached our team. We'll reply to the email address you gave.</Thanks>;
  return (
    <form onSubmit={submit} noValidate className="space-y-5 rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="ct-name" label="Your name" value={f.name} onChange={set("name")} autoComplete="name" testid="contact-name" />
        <TextField id="ct-email" label="Email" type="email" value={f.email} onChange={set("email")} autoComplete="email" testid="contact-email" />
        <TextField id="ct-org" label="School or organisation (optional)" value={f.organisation} onChange={set("organisation")} testid="contact-organisation" />
        <SelectField id="ct-topic" label="Topic" value={f.topic} onChange={set("topic")} options={TOPICS} testid="contact-topic" />
      </div>
      <AutoTextarea id="ct-message" label="Your message" rows={6} value={f.message} onChange={set("message")} testid="contact-message" />
      <Honeypot value={f.website} onChange={set("website")} />
      <p className="text-sm text-ink-soft">Please don't include any pupil names or other details that could identify a child.</p>
      <ErrorText error={error} testid="contact-error" />
      <button type="submit" disabled={busy} className="btn btn-brand" data-testid="contact-submit"><Send size={16} aria-hidden /> {busy ? "Sending…" : "Send message"}</button>
    </form>
  );
};

